import React, { useEffect, useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useSearchParams } from "react-router";

import { apiBaseUrls } from "@platform/constants/shared-constants";

/**
 * GitHub OAuth & GitHub App Callback Page
 *
 * Handles two different callback flows:
 *
 * 1. OAuth App flow (user authentication):
 *    - Called by GitHub after user authorizes the OAuth app
 *    - Query params: code, state → backend exchanges for token
 *    - Backend redirects here with token in query param
 *    - This page sends token to opener window via postMessage
 *
 * 2. GitHub App flow (installation):
 *    - Called by GitHub after user installs the GitHub App
 *    - Query params: installation_id, setup_action, state
 *    - Stores success in localStorage for parent window to detect
 *
 * Flow (OAuth App):
 * 1. Parse token from query parameters
 * 2. Send token to opener window via postMessage
 * 3. Show success message and auto-close
 */
export function GitHubCallbackPage(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [message, setMessage] = useState<string>(
    "Completing GitHub connection...",
  );

  useEffect(() => {
    // Check for OAuth token in URL (from OAuth App flow)
    const token = searchParams.get("token");
    const userId = searchParams.get("userId");

    // Check for GitHub App installation params
    const installationId = searchParams.get("installation_id");
    const setupAction = searchParams.get("setup_action");
    const state = searchParams.get("state");
    const errorCode = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    // Handle OAuth App flow (token present)
    if (token && userId) {
      setStatus("success");
      setMessage("GitHub authentication successful!");

      // Send token to opener window
      if (window.opener) {
        window.opener.postMessage(
          {
            type: "GITHUB_OAUTH_SUCCESS",
            token,
            user: { id: userId },
          },
          window.location.origin,
        );
      }

      // Auto-close after short delay
      setTimeout(() => {
        if (window.opener) {
          window.close();
        }
      }, 1500);

      return;
    }

    // Handle error from GitHub
    if (errorCode) {
      setStatus("error");
      setMessage(
        errorDescription ||
          `Authentication failed: ${errorCode}` ||
          "GitHub authentication was cancelled or failed.",
      );

      if (window.opener) {
        window.opener.postMessage(
          {
            type: "GITHUB_OAUTH_ERROR",
            error: { message: message },
          },
          window.location.origin,
        );
      }

      return;
    }

    // Handle GitHub App installation flow (installation_id present)
    if (installationId) {
      // Installation successful
      if (setupAction === "install" || setupAction === "update") {
        // Call the API to create the VCS connection (public endpoint with state)
        void (async () => {
          try {
            await axios.post(
              `${apiBaseUrls.BACKEND}/api/v1/vcs-connections/github-app-callback`,
              {
                installationId,
                state,
              },
            );
          } catch {
            // Failed to save VCS connection silently
          }
        })();

        // Store in localStorage for parent window to detect
        localStorage.setItem(
          "github-install-success",
          JSON.stringify({
            installationId,
            state,
            timestamp: Date.now(),
          }),
        );

        setStatus("success");
        setMessage(
          "GitHub App installed successfully! You can close this window.",
        );

        // Notify opener window via postMessage (for popup flow)
        if (window.opener) {
          window.opener.postMessage(
            {
              type: "GITHUB_INSTALL_SUCCESS",
              installationId,
              state,
            },
            window.location.origin,
          );
        }

        // Auto-close after 2 seconds if this is a popup
        setTimeout(() => {
          if (window.opener) {
            window.close();
          }
        }, 2000);
      } else {
        setStatus("error");
        setMessage(`Unexpected setup action: ${setupAction || "unknown"}`);
      }

      return;
    }

    // No recognized params - could be direct navigation
    if (!installationId && !token) {
      setStatus("error");
      setMessage(
        "Invalid callback: no authentication data found. Please try again.",
      );
    }
  }, [searchParams]);

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else {
      // Fallback: navigate back or close attempt
      window.history.back();
    }
  };

  const handleManualRetry = () => {
    window.location.reload();
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={2} sx={{ p: 4, textAlign: "center" }}>
        {status === "loading" && (
          <Stack spacing={3} alignItems="center">
            <CircularProgress size={48} />
            <Typography variant="h5" gutterBottom>
              Connecting to GitHub
            </Typography>
            <Typography color="text.secondary">{message}</Typography>
          </Stack>
        )}

        {status === "success" && (
          <Stack spacing={3} alignItems="center">
            <CheckCircleIcon color="success" sx={{ fontSize: 64 }} />
            <Typography variant="h5" gutterBottom>
              Successfully Connected!
            </Typography>
            <Typography color="text.secondary">{message}</Typography>
            <Typography variant="caption" color="text.secondary">
              This window will close automatically...
            </Typography>
            <Button variant="outlined" onClick={handleClose}>
              Close Window
            </Button>
          </Stack>
        )}

        {status === "error" && (
          <Stack spacing={3} alignItems="center">
            <ErrorIcon color="error" sx={{ fontSize: 64 }} />
            <Typography variant="h5" gutterBottom>
              Connection Failed
            </Typography>
            <Typography color="text.secondary">{message}</Typography>
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button variant="outlined" onClick={handleClose}>
                Go Back
              </Button>
              <Button variant="contained" onClick={handleManualRetry}>
                Try Again
              </Button>
            </Box>
          </Stack>
        )}
      </Paper>
    </Container>
  );
}

export default GitHubCallbackPage;
